import {
    hashKey,
    notifyManager,
    useQuery,
    UseQueryOptions,
} from "@tanstack/react-query";
import { useScope } from "@state/scopeContext.lib";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { queryClient } from "queryClient";
import { getUserEntities } from "@api/user.api";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { fetchEntitiesAccounts } from "@queries/fetchEntitiesAccounts";
import { clearEntityContextsConnectionErrors } from "@helpers/connectionError";
import { getTotalBalance } from "./useFinancialAccounts";
import { useActiveWorkspace } from "./useActiveWorkspace";
import { useCanPerformWorkspaceAction } from "./permissions/useCanPerformWorkspaceAction";

export enum ExcludeEmptyMockEntitiesMode {
    NEVER = "never",
    REQUIRE_ACCOUNTS = "requireAccounts",
    REQUIRE_TRANSACTIONS = "requireTransactions",
}

export interface UseEntitiesContextsOptions {
    requireAdminAccess?: boolean;
    onlyBusiness?: boolean;
    onlyPersonal?: boolean;
    onlyWithBalance?: boolean;
    excludeMockEntities?: boolean;
    excludeEmptyMockEntities?: ExcludeEmptyMockEntitiesMode;
}

type TQueryResult = Awaited<ReturnType<typeof getUserEntities>>;

export function useEntitiesContexts(
    {
        requireAdminAccess,
        onlyBusiness,
        onlyPersonal,
        onlyWithBalance,
        excludeMockEntities,
        excludeEmptyMockEntities = ExcludeEmptyMockEntitiesMode.REQUIRE_ACCOUNTS,
    }: UseEntitiesContextsOptions = {},
    queryOptions?: Omit<UseQueryOptions<TQueryResult>, "queryKey">,
) {
    const { workspaceId } = useScope();
    const isDemoWorkspace = useActiveWorkspace()?.isDemo ?? false;
    const canManageEntities = useCanPerformWorkspaceAction(
        WorkspaceAction.ENTITY_SETTINGS_EDIT,
    );

    const query = useQuery<TQueryResult>({
        queryKey: getEntitiesAccountsQueryKey(workspaceId ?? ""),
        queryFn: async () => await fetchEntitiesAccounts(workspaceId ?? null),
        ...(queryOptions ?? {}),
        refetchOnMount: false,
        enabled: !!workspaceId && queryOptions?.enabled !== false,
    });

    return useMemo(() => {
        if (!query.data) {
            return undefined;
        }

        let contexts = query.data.entities;

        if (requireAdminAccess && !canManageEntities) {
            return [];
        }

        if (onlyBusiness) {
            contexts = contexts.filter((e) => e.entity.isBusiness);
        }
        if (onlyPersonal) {
            contexts = contexts.filter((e) => e.entity.isPersonal);
        }
        if (excludeMockEntities) {
            contexts = contexts.filter((e) => !e.entity.isMockEntity);
        }
        if (excludeEmptyMockEntities !== ExcludeEmptyMockEntitiesMode.NEVER) {
            contexts = contexts.filter(
                (e) =>
                    !e.entity.isMockEntity ||
                    (e.financialAccounts.length > 0 &&
                        excludeEmptyMockEntities ===
                            ExcludeEmptyMockEntitiesMode.REQUIRE_ACCOUNTS) ||
                    (e.mockEntityIncludesTransactions &&
                        excludeEmptyMockEntities ===
                            ExcludeEmptyMockEntitiesMode.REQUIRE_TRANSACTIONS),
            );
        }
        if (onlyWithBalance) {
            contexts = contexts.filter(
                (e) => getTotalBalance(e.financialAccounts) > 0,
            );
        }

        if (isDemoWorkspace) {
            contexts = clearEntityContextsConnectionErrors(contexts);
        }

        return contexts;
    }, [
        query.data,
        requireAdminAccess,
        canManageEntities,
        onlyBusiness,
        onlyPersonal,
        excludeMockEntities,
        excludeEmptyMockEntities,
        onlyWithBalance,
        isDemoWorkspace,
    ]);
}

/** The entities query's state, re-rendering the caller when it changes. */
export function useEntitiesContextsQueryState() {
    const { workspaceId } = useScope();
    const queryKey = getEntitiesAccountsQueryKey(workspaceId ?? "");
    const queryHash = hashKey(queryKey);

    // The cache notifies synchronously, also while another component renders
    // (a `useQuery` building its query), so updates go through `batchCalls`.
    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            queryClient.getQueryCache().subscribe(
                notifyManager.batchCalls((event) => {
                    if (event.query.queryHash === queryHash) {
                        onStoreChange();
                    }
                }),
            ),
        [queryHash],
    );

    return useSyncExternalStore(subscribe, () =>
        queryClient.getQueryState(queryKey),
    );
}
